import React, { useState } from 'react';
import { Form, InputNumber, Select, Typography } from 'antd';

const { Text } = Typography;

const modeOptions = [
  { label: 'Percentage', value: 'percentage' },
  { label: 'Amount (£)', value: 'amount' },
]

const periodOptions = [
  { label: 'Annual', value: 'annual' },
  { label: 'Monthly', value: 'monthly' },
]

const to2 = (n) => {
  const num = Number(n) || 0
  return Math.round(num * 100) / 100
}

const PensionContributionInput = ({ 
  onChange, 
  baseSalary,
  label
}) => {
  const [inputMode, setInputMode] = useState('percentage'); // 'percentage' or 'amount'
  const [periodMode, setPeriodMode] = useState('annual'); // 'annual' or 'monthly'
  const form = Form.useFormInstance();

  const calculateAmount = (percentage) => {
    if (!baseSalary) return 0;
    const annualAmount = (percentage / 100) * baseSalary;
    return periodMode === 'annual' ? annualAmount : annualAmount / 12;
  };

  const calculatePercentage = (amount) => {
    if (!baseSalary) return 0;
    const annualAmount = periodMode === 'monthly' ? amount * 12 : amount;
    return (annualAmount / baseSalary) * 100;
  };

  const handleInputChange = (newValue) => {
    // Allow transient empty/undefined while typing without forcing 0
    if (newValue === '' || newValue === null || newValue === undefined) {
      onChange(0)
      return
    }
    const rounded = to2(newValue)
    const newPercentage = inputMode === 'percentage' 
      ? rounded 
      : calculatePercentage(rounded);
    onChange(newPercentage);
  };

  const handleModeChange = (newMode) => {
    setInputMode(newMode);
    const currentValue = form.getFieldValue('employeePensionContribution');
    if (newMode === 'percentage') {
      const newPercentage = calculatePercentage(currentValue);
      form.setFieldValue('employeePensionContribution', to2(newPercentage));
      onChange(newPercentage);
    } else {
      const newAmount = calculateAmount(currentValue);
      form.setFieldValue('employeePensionContribution', to2(newAmount));
      onChange(currentValue);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriodMode(newPeriod);
    if (inputMode === 'amount') {
      const currentValue = form.getFieldValue('employeePensionContribution');
      const newAmount = newPeriod === 'annual' 
        ? to2(currentValue * 12) 
        : to2(currentValue / 12);
      form.setFieldValue('employeePensionContribution', newAmount);
      onChange(calculatePercentage(newAmount));
    }
  };

  return (
    <Form.Item label={label}>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Select
            value={inputMode}
            onChange={handleModeChange}
            options={modeOptions}
            className="w-40"
          />
          {inputMode === 'amount' && (
            <Select
              value={periodMode}
              onChange={handlePeriodChange}
              options={periodOptions}
              className="w-32"
            />
          )}
        </div>

        <Form.Item name="employeePensionContribution" noStyle>
          <InputNumber
            style={{ width: '100%', height: 40 }}
            onChange={handleInputChange}
            stringMode
            precision={2}
            formatter={value => 
              inputMode === 'percentage' 
                ? `${value}%` 
                : `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={value => 
              inputMode === 'percentage'
                ? value.replace('%', '')
                : value.replace(/£|\s|,/g, '')
            }
            step={inputMode === 'percentage' ? 0.1 : 10}
            min={0}
            max={inputMode === 'percentage' ? 100 : undefined}
          />
        </Form.Item>

        {baseSalary && (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {inputMode === 'percentage' 
              ? `Equivalent to £${to2(calculateAmount(form.getFieldValue('employeePensionContribution'))).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${periodMode === 'annual' ? 'per year' : 'per month'}`
              : `Equivalent to ${to2(calculatePercentage(form.getFieldValue('employeePensionContribution'))).toLocaleString(undefined, { maximumFractionDigits: 2 })}% of salary`}
          </Text>
        )}
      </div>
    </Form.Item>
  );
};

export default PensionContributionInput;